import type { FieldInputProps, FieldMetaProps } from 'formik';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { usePrevious } from 'src/hooks/use-previous';

type GlobalError = true;

/**
 * With an `OnShowError` function, you can control when an error should be shown or not.
 * E.g we sometimes _don't_ want to show an error _while_ the user is typing. This can
 * happen if the user enters an email address which would _always_ be invalid initially.
 */
export type OnShowError = (error: string | GlobalError) => boolean;

// global form errors should be set via `setStatus(true)`
// server side validation errors should be set via `setStatus({ [field.name]: 'message' })`
export function useErrorHandler(
  field: FieldInputProps<unknown>,
  meta: FieldMetaProps<unknown>,
  onShowError?: OnShowError
) {
  const { submitCount, status, setStatus } = useFormikContext();
  const prevValue = usePrevious(field.value);

  const globalError = status === true;
  const serverError: string | undefined =
    typeof status === 'object' ? status[field.name] : undefined;

  let showError = Boolean(
    globalError || serverError || (meta.error && (meta.touched || submitCount))
  );

  if (showError && onShowError) {
    const error = globalError || serverError! || meta.error!;
    showError = onShowError(error);
  }

  // reset server side validation error on change
  useEffect(() => {
    if (serverError && field.value !== prevValue) {
      const newStatus = { ...status };
      delete newStatus[field.name];
      setStatus(newStatus);
    }
  }, [serverError, field.value, prevValue, setStatus, status, field.name]);

  // in case of a global error, the error message can actually be undefined
  const error = serverError || meta.error;

  return [showError, error] as const;
}
